import {
  ConfigProvider,
  Pagination,
  Select,
  Table,
  TableColumnsType,
} from "antd";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FieldData } from "../../components/ApplicationCreate/ApplicationCreate";
import { FilterAgentForm } from "../../components/ListAgent/FilterAgentForm/FilterAgentForm";
import { selectViewShowBy } from "../../constants/selectViewShowBy";
import { IConstructorJsxFilteredListAgent } from "../../interfaces/Constructor/IConstructorJsxFilteredListAgent";
import { IListAgentTable } from "../../interfaces/ListTable/IListAgentTable";
import { useArchiveAgentMutation } from "../../store/api/listAgent.api";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import {
  SELECT_AGENT,
  SELECT_AGENT_BY_ID,
  SELECT_LIST_AGENT_ACTIVE,
  SELECT_LIST_AGENT_ARCHIVE,
} from "../../store/selectors/listAgentSelector";
import {
  CHANGE_AGENT_FORM_DISABLE,
  CHANGE_AGENT_FORM_INITIAL,
  CHANGE_AGENT_FORM_TYPE,
  CHANGE_SELECTED_AGENT,
  IAgent,
} from "../../store/slice/listAgentSlice/listAgentSlice";
import { UI_ITOModuleTitle } from "../../UI_ITO/indexUI";

import classes from "./PageListAgent.module.css";

/**
 * Компонента страницы списка агентов
 */
export function PageListAgent(): ReactElement {
  const { id } = useParams();
  const findAgent = useAppSelector(SELECT_AGENT_BY_ID(id ? +id : undefined));
  const dispatch = useAppDispatch();
  const listAgentActive = useAppSelector(SELECT_LIST_AGENT_ACTIVE);
  const listAgentArchive = useAppSelector(SELECT_LIST_AGENT_ARCHIVE);
  const selectedAgent = useAppSelector(SELECT_AGENT);
  const [isModalMoveToArchiveOpen, setIsModalMoveToArchiveOpen] =
    useState(false);
  const [filteredListAgentActive, setFilteredListAgentActive] =
    useState<IAgent[]>(listAgentActive);
  const [filteredListAgentArchive, setFilteredListAgentArchive] =
    useState<IAgent[]>(listAgentArchive);
  const [agentConstructor, setAgentConstructor] =
    useState<keyof IConstructorJsxFilteredListAgent>("LIST");
  const [isOrganisationContained, setIsOrganisationContained] = useState(
    Boolean(selectedAgent?.organization_name),
  );
  const [archiveAgent, { isError: archiveAgentError }] =
    useArchiveAgentMutation();

  function changeSelectedAgent(agent: IAgent) {
    dispatch(CHANGE_AGENT_FORM_INITIAL(agent));
    dispatch(CHANGE_SELECTED_AGENT(agent));
    dispatch(CHANGE_AGENT_FORM_DISABLE(true));
    dispatch(CHANGE_AGENT_FORM_TYPE("INFO"));
    if (agent.organization) {
      setIsOrganisationContained(true);
    } else {
      setIsOrganisationContained(false);
    }
  }

  // отображение агентов в архивном и активном списке, установка найденного пользователя
  useEffect(() => {
    if (findAgent) {
      changeSelectedAgent(findAgent);
    }
  }, [listAgentActive, listAgentArchive]);

  function editAgentInfo() {
    dispatch(CHANGE_AGENT_FORM_DISABLE(false));
    dispatch(CHANGE_AGENT_FORM_TYPE("EDIT"));
  }
  function stopEditAgentInfo() {
    dispatch(CHANGE_AGENT_FORM_DISABLE(true));
    dispatch(CHANGE_AGENT_FORM_TYPE("INFO"));
  }

  function handleSearch(searchFormData: FieldData[]) {
    setFilteredListAgentActive(() => {
      if (searchFormData[0]?.value) {
        const searchText = searchFormData[0].value as string;
        return listAgentActive.filter(
          (agent: IAgent) =>
            agent?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            agent?.surname?.toLowerCase().includes(searchText.toLowerCase()) ||
            agent?.patronymic
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            String(agent?.inn).includes(searchText),
        );
      } else {
        return listAgentActive;
      }
    });
    setFilteredListAgentArchive(() => {
      if (searchFormData[0]?.value) {
        const searchText = searchFormData[0].value as string;
        return listAgentArchive.filter(
          (agent: IAgent) =>
            agent?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            agent?.surname?.toLowerCase().includes(searchText.toLowerCase()) ||
            agent?.patronymic
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            String(agent?.inn).includes(searchText),
        );
      } else {
        return listAgentArchive;
      }
    });
    if (filteredListAgentActive?.length === 0) {
      setAgentConstructor("EMPTY");
    } else {
      setAgentConstructor("LIST");
    }
  }

  const navigate = useNavigate();

  const columnsApplication: TableColumnsType<IListAgentTable> = [
    {
      title: "Наименование организации",
      width: 300,
      dataIndex: "organizationName",
      render: (organizationName) => (
        <span
          style={{ color: "#3c79ff", cursor: "pointer" }}
          onClick={() => {
            navigate(`/agent/${organizationName.id}`);
          }}
        >
          {organizationName.name}
        </span>
      ),
    },
    {
      title: "ИНН",
      dataIndex: "inn",
      width: 75,
    },
    {
      title: "Контактное лицо",
      dataIndex: "fullName",
      width: 120,
    },
    {
      title: "Контактный номер",
      dataIndex: "phoneNumber",
      width: 125,
    },

    {
      title: "Электронная почта",
      dataIndex: "email",
      width: 1,
    },
  ];

  const dataTableAgentSource: IListAgentTable[] = filteredListAgentActive?.map(
    (item: IAgent) => {
      const {
        id,
        organization,
        inn,
        surname,
        name,
        patronymic,
        phone_number,
        email,
      } = item;

      return {
        key: id,
        organizationName: { name: organization?.name || "", id },
        email,
        id,
        fullName: `${surname} ${name[0].toUpperCase()}.${patronymic && patronymic?.length > 0 ? `${patronymic[0].toUpperCase()}.` : ""}`,
        inn: organization?.inn || "",
        phoneNumber: phone_number ? phone_number : "",
      };
    },
  );

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            borderRadius: 20,
            rowHoverBg: "#eaeaea",
            headerBorderRadius: 12,
            headerBg: "transparent",
            rowExpandedBg: "transparent",
            colorBorder: "black",
            colorBorderBg: "black",
          },
          Button: {
            defaultBorderColor: "rgb(0 0 0 / 20%)",
            defaultColor: "rgba(0, 0, 0, 0.65)",
          },
        },
      }}
    >
      <>
        <div className={classes.listAgent}>
          <div className={classes.listAgentPageHeader}>
            <UI_ITOModuleTitle title="Агенты" />
            <FilterAgentForm />
          </div>

          <div className={classes.agentListContainer}>
            <Table
              className={classes.listAgentTable}
              rowClassName={classes.tableRow}
              columns={columnsApplication}
              dataSource={dataTableAgentSource}
              pagination={false}
              size="small"
            />
          </div>

          <div className={classes.paginationContainer}>
            <div>
              Показывать по:{" "}
              <Select
                options={selectViewShowBy}
                className={classes.paginationSelect}
              />
            </div>

            <Pagination
              className={classes.pagination}
              total={10}
              defaultPageSize={2}
            />
          </div>
        </div>
      </>
    </ConfigProvider>
  );
}
